import { supabase } from './supabase';
import { leadsService, tasksService, notificationsService, chatService } from './supabase-service';
import { sendSMS } from './sms-service';
import { processPrompt } from './gemini';
import { Node, Edge } from '@xyflow/react';

import * as emailSummaryService from './email-summary-service';
async function getStore() {
  const mod = await import('../store/useStore');
  return mod.useStore.getState();
}

export type AutomationEventType = 
  | 'LEAD_CREATED' 
  | 'TASK_STATUS_CHANGED' 
  | 'LEAD_STATUS_CHANGED'
  | 'LEAD_SCORE_HIGH'
  | 'LEAD_SCORE_CHANGED'
  | 'LEAD_TAG_ADDED'
  | 'EMAIL_OPENED' 
  | 'SMS_RECEIVED' 
  | 'DEAL_WON' 
  | 'LEAD_INACTIVITY' 
  | 'CALENDAR_EVENT_CREATED' 
  | 'DOCUMENT_SIGNED' 
  | 'OFFER_SUBMITTED'
  | 'OFFER_ACCEPTED'
  | 'CONTRACT_SIGNED'
  | 'SCHEDULED_MONTHLY'
  | 'UNSUBSCRIBED'
  | 'FACEBOOK_LEAD'
  | 'CLIENT_BIRTHDAY'
  | 'REFERRAL_REQUESTED'
  | 'TASK_COMPLETED'
  | 'EMAIL_RECEIVED'
  | 'WEBHOOK_RECEIVED';

class AutomationEngine {
  private static instance: AutomationEngine;

  private constructor() {}

  public static getInstance(): AutomationEngine {
    if (!AutomationEngine.instance) {
      AutomationEngine.instance = new AutomationEngine();
    }
    return AutomationEngine.instance;
  }

  /**
   * Trigger an automation event
   */
  public async trigger(eventType: AutomationEventType, data: any) {
    const leadId = data.id || data.leadId || data.lead_id;
    console.log(`[Automation Engine] Received event: ${eventType} for lead ${leadId || 'N/A'}`);
    
    try {
      if (!supabase) return;

      // 1. Handle OS Preference-based direct alerts (Hardcoded system alerts)
      await this.handlePreferenceAlerts(eventType, data);

      // 1b. Handle AI Auto-Responder
      if (eventType === 'SMS_RECEIVED') {
        await this.handleAIAutoResponder(data);
      }

      // 2. Fetch active workflows for the current user only
      const store = await getStore();
      const userId = store.currentUser?.id || data.user_id || data.assigned_to;
      
      let query = supabase
        .from('user_automations')
        .select('*')
        .eq('is_active', true);
      
      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data: workflows, error } = await query;

      if (error) throw error;
      if (!workflows || workflows.length === 0) {
        console.log(`[Automation Engine] No matching automations for this event.`);
        return;
      }

      console.log(`[Automation Engine] Checking ${workflows.length} automations for matching triggers`);

      // 3. Process each matching workflow
      for (const workflow of workflows) {
        const nodes = workflow.nodes as Node[];
        const edges = workflow.edges as Edge[];

        // Find trigger node
        const triggerNode = nodes.find(n => n.data?.type === 'trigger' && this.matchesTrigger(n, eventType, data));
        
        if (triggerNode) {
          // Set running state
          (await getStore()).setIsAutomationRunning(true);
          
          console.log(`[Automation Engine] Automation "${workflow.name}" triggered by event. Walking graph...`);
          /* toast.success(`Automation: ${workflow.name} triggered`, {
            icon: '⚡',
            style: {
              borderRadius: '10px',
              background: 'var(--t-surface)',
              color: 'var(--t-primary)',
              border: '1px solid var(--t-primary)'
            },
          }); */
          
          try {
            await this.executeFromNode(triggerNode, nodes, edges, data);
            // Record success
            this.recordExecution(workflow.id, true).catch(console.error);
            this.logExecution(workflow.id, workflow.user_id, 'success', eventType, { trigger: triggerNode.data?.label }).catch(console.error);
          } catch (execErr) {
            // Record failure
            this.recordExecution(workflow.id, false).catch(console.error);
            this.logExecution(workflow.id, workflow.user_id, 'failure', eventType, { error: String(execErr) }).catch(console.error);
            console.error(`[AutomationEngine] Workflow execution failed:`, execErr);
          }
          
          // Small delay for visual feedback of "Running"
          setTimeout(async () => {
            (await getStore()).setIsAutomationRunning(false);
          }, 2000);
        }
      }
    } catch (err) {
      console.error('[AutomationEngine] Error processing triggers:', err);
      (await getStore()).setIsAutomationRunning(false);
    }
  }

  /**
   * Records execution stats back to Supabase
   */
  private async recordExecution(workflowId: string, success: boolean) {
    try {
      if (!supabase) return;

      const { data: current } = await supabase
        .from('user_automations')
        .select('run_count, success_count')
        .eq('id', workflowId)
        .single();

      if (current) {
        const run_count = (current.run_count || 0) + 1;
        const success_count = (current.success_count || 0) + (success ? 1 : 0);
        const success_rate = Math.round((success_count / run_count) * 100);

        await supabase
          .from('user_automations')
          .update({
            run_count,
            success_count,
            success_rate,
            last_triggered_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', workflowId);
      }
    } catch (err) {
      console.error('[AutomationEngine] Failed to record execution:', err);
    }
  }

  /**
   * Writes a log entry to the automation_logs table for history tracking.
   */
  private async logExecution(workflowId: string, userId: string, status: 'success' | 'failure', triggerEvent: string, details: Record<string, unknown>) {
    try {
      if (!supabase) return;
      
      await supabase
        .from('automation_logs')
        .insert([{
          automation_id: workflowId,
          user_id: userId,
          status,
          trigger_event: triggerEvent,
          details,
          executed_at: new Date().toISOString(),
        }]);
    } catch (err) {
      console.error('[AutomationEngine] Failed to log execution:', err);
    }
  }

  /**
   * Dispatches OS Message alerts based on user preferences table
   */
  private async handlePreferenceAlerts(eventType: AutomationEventType, data: any) {
    const storeModule = await import('../store/useStore');
    const store = storeModule.useStore.getState();
    const currentUser = store.currentUser;
    const userId = (data.user_id as string) || (data.assigned_to as string) || currentUser?.id;
    const leadId = data.id || data.leadId || data.lead_id;

    if (!userId) return;

    try {
      // Fetch user preferences
      const { data: prefs, error } = await supabase
        .from('user_os_messages_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows found"


      switch (eventType) {
        case 'LEAD_CREATED':
          if (prefs?.new_lead_alerts_enabled) {
            await emailSummaryService.sendLeadAlert(userId, leadId, 'new');
          }
          break;
        case 'LEAD_SCORE_HIGH':
          if (prefs?.lead_score_high_alert_enabled) {
            await emailSummaryService.sendLeadAlert(userId, leadId, 'high-score');
          }
          break;
        case 'LEAD_STATUS_CHANGED':
          if (data.status === 'closed-won' && prefs?.deal_closed_alerts_enabled) {
            await emailSummaryService.sendDealAlert(userId, leadId, 'closed');
          }
          break;
        case 'OFFER_SUBMITTED':
          if (prefs?.offer_made_alert_enabled) {
            await emailSummaryService.sendOfferAlert(userId, leadId, 'made');
          }
          break;
        case 'OFFER_ACCEPTED':
          if (prefs?.offer_accepted_alert_enabled) {
            await emailSummaryService.sendOfferAlert(userId, leadId, 'accepted');
          }
          break;
        case 'CONTRACT_SIGNED':
          if (prefs?.contract_signed_alert_enabled) {
            await emailSummaryService.sendContractAlert(userId, leadId);
          }
          break;
        case 'TASK_STATUS_CHANGED':
          if (data.status === 'overdue' && prefs?.task_overdue_alert_enabled) {
            const taskId = data.id || data.taskId;
            if (taskId) await emailSummaryService.sendTaskAlert(userId, taskId, 'overdue');
          }
          break;
        case 'LEAD_INACTIVITY':
          if (prefs?.lead_inactivity_alert_enabled) {
            await emailSummaryService.checkLeadInactivity(userId);
          }
          break;
        case 'CALENDAR_EVENT_CREATED':
          // Optional: handle calendar preferences if needed
          break;
      }
    } catch (err) {
      console.error('[AutomationEngine] Preference alert failed:', err);
    }
  }

  private matchesTrigger(node: Node, eventType: AutomationEventType, data: any): boolean {
    const config = node.data;
    if (!config) return false;
    
    switch (eventType) {
      case 'LEAD_CREATED': 
        return config.triggerType === 'new_lead';
      case 'TASK_STATUS_CHANGED': 
        return config.triggerType === 'task_overdue' && data.status === 'overdue' ||
               config.triggerType === 'status_change' && data.status === config.status;
      case 'LEAD_STATUS_CHANGED': 
        return config.triggerType === 'status_change' && data.status === config.status;
      case 'LEAD_SCORE_HIGH': 
        return config.triggerType === 'high_score' && Number(data.deal_score || data.dealScore || 0) >= Number(config.threshold || 80);
      case 'LEAD_SCORE_CHANGED':
        return config.triggerType === 'lead_score_changed';
      case 'LEAD_TAG_ADDED':
        return config.triggerType === 'lead_tag_added';
      case 'EMAIL_OPENED': 
        return config.triggerType === 'email_opened';
      case 'SMS_RECEIVED': 
        return config.triggerType === 'sms_received';
      case 'DEAL_WON': 
        return config.triggerType === 'status_change' && data.status === 'closed-won';
      case 'LEAD_INACTIVITY': 
        return config.triggerType === 'inactivity';
      case 'CALENDAR_EVENT_CREATED': 
        return config.triggerType === 'new_event';
      case 'DOCUMENT_SIGNED': 
        return config.triggerType === 'doc_signed';
      case 'OFFER_SUBMITTED':
        return config.triggerType === 'offer_submitted';
      case 'OFFER_ACCEPTED':
        return config.triggerType === 'offer_accepted';
      case 'CONTRACT_SIGNED':
        return config.triggerType === 'contract_signed';
      case 'SCHEDULED_MONTHLY': 
        return config.triggerType === 'monthly_run';
      case 'UNSUBSCRIBED': 
        return config.triggerType === 'unsubscribed';
      case 'FACEBOOK_LEAD': 
        return config.triggerType === 'facebook_lead';
      case 'CLIENT_BIRTHDAY':
        return config.triggerType === 'birthday';
      case 'REFERRAL_REQUESTED':
        return config.triggerType === 'referral_request';
      case 'TASK_COMPLETED':
        return config.triggerType === 'task_completed' || (config.triggerType === 'status_change' && data.status === 'done');
      case 'EMAIL_RECEIVED':
        return config.triggerType === 'email_received';
      case 'WEBHOOK_RECEIVED':
        return config.triggerType === 'webhook_received';
      default: return false;
    }
  }

  private async executeFromNode(currentNode: Node, allNodes: Node[], allEdges: Edge[], context: any) {
    const childEdges = allEdges.filter(e => e.source === currentNode.id);
    
    for (const edge of childEdges) {
      const targetNode = allNodes.find(n => n.id === edge.target);
      if (targetNode) {
        // Condition node check: evaluate and skip children if false
        if (targetNode.data?.type === 'condition') {
          const passes = this.evaluateCondition(targetNode, context);
          console.log(`[AutomationEngine] Condition "${targetNode.data?.label}": ${passes ? 'PASS ✓' : 'FAIL ✗'}`);
          if (!passes) continue; // Skip this branch
        }
        
        const result = await this.runNodeAction(targetNode, context);
        await this.executeFromNode(targetNode, allNodes, allEdges, { ...context, ...result });
      }
    }
  }

  /**
   * Evaluates a condition node against the current context.
   */
  private evaluateCondition(node: Node, context: any): boolean {
    const config = node.data;
    if (!config) return true; // No config = pass through

    const field = config.conditionField as string;
    const operator = config.conditionOperator as string;
    const targetValue = config.conditionValue as string;

    if (!field || !operator || targetValue === undefined) return true;

    const actualValue = context[field] ?? '';
    const numActual = Number(actualValue);
    const numTarget = Number(targetValue);

    switch (operator) {
      case 'equals':
      case '=':
        return String(actualValue).toLowerCase() === targetValue.toLowerCase();
      case 'not_equals':
      case '!=':
        return String(actualValue).toLowerCase() !== targetValue.toLowerCase();
      case 'greater_than':
      case '>':
        return !isNaN(numActual) && !isNaN(numTarget) && numActual > numTarget;
      case 'less_than':
      case '<':
        return !isNaN(numActual) && !isNaN(numTarget) && numActual < numTarget;
      case 'contains':
        return String(actualValue).toLowerCase().includes(targetValue.toLowerCase());
      case 'not_contains':
        return !String(actualValue).toLowerCase().includes(targetValue.toLowerCase());
      default:
        return true;
    }
  }

  private async runNodeAction(node: Node, context: any) {
    const config = node.data;
    if (!config) return;

    console.log(`[Automation Engine] Executing node: ${config.actionType || config.type} (${config.label}) with data:`, config);

    try {
      const store = await getStore();
      const { sendAutomationSms, sendAutomationEmail, currentUser } = store;

      const targetId = context.id || context.leadId || context.lead_id;

      switch (config.actionType) {
        case 'send_sms':
          if (targetId) {
            const msg = this.parseTemplate(config.message as string, { ...context, agent_name: currentUser?.name || 'Your Agent' });
            await sendAutomationSms(targetId as string, msg);
          }
          break;

        case 'send_email':
          if (targetId) {
            const subject = this.parseTemplate(config.subject as string, { ...context, agent_name: currentUser?.name || 'Your Agent' });
            const content = this.parseTemplate(config.message as string, { ...context, agent_name: currentUser?.name || 'Your Agent' });
            await sendAutomationEmail(targetId as string, subject, content);
          }
          break;

        case 'notify': {
          const userId = (context.user_id as string) || (context.assigned_to as string) || currentUser?.id;
          if (userId) {
            await notificationsService.create({
              user_id: userId,
              type: 'system',
              title: (config.label as string) || 'Automation Alert',
              message: this.parseTemplate(config.message as string, context),
              link: targetId ? `/leads/${targetId}` : undefined
            });
          }
          break;
        }

        case 'assign_lead':
          if (targetId && config.agentId) {
            await leadsService.update(targetId as string, { assigned_to: config.agentId });
          }
          break;

        case 'update_status':
          if (targetId && config.status) {
            await leadsService.update(targetId as string, { status: config.status });
          }
          break;



        case 'add_task':
          if (targetId) {
            await tasksService.create({
              title: this.parseTemplate(config.taskTitle as string, context),
              description: this.parseTemplate(config.description as string, context),
              priority: (config.priority as any) || 'medium',
              assigned_to: (config.agentId as string) || (context.assigned_to as string) || currentUser?.id,
              lead_id: targetId as string,
              due_date: new Date(Date.now() + 86400000).toISOString()
            });
          }
          break;

        case 'delay': {
          let seconds = Number(config.duration || config.delaySeconds || 0);
          const unit = config.delayUnit || 'seconds';
          
          if (unit === 'minutes') seconds *= 60;
          if (unit === 'hours') seconds *= 3600;
          if (unit === 'days') seconds *= 86400;

          console.log(`[AutomationEngine] Delaying for ${seconds} seconds (${config.duration} ${unit})...`);
          if (seconds > 0) {
            await new Promise(resolve => setTimeout(resolve, seconds * 1000));
          }
          break;
        }
        case 'ai': {
          const promptTemplate = config.prompt as string || 'Analyze this data: {{name}}';
          const model = config.model as string || 'gemini-2.0-flash-lite';
          console.log(`[AutomationEngine] Running AI processing with ${model}...`);
          
          const fullPrompt = this.parseTemplate(promptTemplate, context);
          
          // Lazy load processPrompt to break circular dependency
          const gemini = await import('./gemini');
          const aiResponse = await gemini.processPrompt(fullPrompt, context, model);
          
          const result = { ai_output: aiResponse.response };
          console.log(`[Automation Engine] Node result:`, result);
          return result;
        }

        case 'call_webhook':
          if (config.webhookUrl) {
            console.log(`[AutomationEngine] Calling webhook: ${config.webhookUrl}`);
            await fetch(config.webhookUrl as string, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...context, automation_triggered_at: new Date().toISOString() })
            });
          }
          break;

        case 'update_score':
          if (targetId && config.scoreValue !== undefined) {
            console.log(`[AutomationEngine] Updating score for lead ${targetId} to ${config.scoreValue}`);
            await leadsService.update(targetId as string, { deal_score: Number(config.scoreValue) });
          }
          break;

        case 'create_note':
          if (targetId && config.noteText) {
            console.log(`[AutomationEngine] Creating note for lead ${targetId}`);
            if (supabase) {
              await supabase.from('lead_notes').insert([{
                lead_id: targetId,
                user_id: currentUser?.id,
                content: this.parseTemplate(config.noteText as string, context)
              }]);
            }
          }
          break;

        case 'add_tag':
          if (targetId && (config.tag || config.tagName)) {
            const tagValue = (config.tagName || config.tag) as string;
            const action = config.tagAction || 'add';
            console.log(`[AutomationEngine] ${action} tag "${tagValue}" to lead ${targetId}`);
            
            const { data: lead } = await supabase.from('leads').select('tags').eq('id', targetId).single();
            let currentTags = lead?.tags || [];
            
            if (action === 'add' && !currentTags.includes(tagValue)) {
              await leadsService.update(targetId as string, { tags: [...currentTags, tagValue] });
            } else if (action === 'remove' && currentTags.includes(tagValue)) {
              await leadsService.update(targetId as string, { tags: currentTags.filter((t: string) => t !== tagValue) });
            }
          }
          break;

        case 'send_slack':
        case 'send_chat':
          if (config.channelId || config.webhookUrl) {
            const msg = this.parseTemplate(config.message as string, context);
            if (config.webhookUrl) {
               await fetch(config.webhookUrl as string, {
                 method: 'POST',
                 headers: { 'Content-Type': 'application/json' },
                 body: JSON.stringify({ text: msg })
               });
            } else if (config.channelId) {
              await chatService.sendMessage({
                channel_id: config.channelId as string,
                content: msg,
                sender_name: 'Automation Bot'
              });
            }
          }
          break;
      }
      console.log(`[Automation Engine] Node completed successfully.`);
    } catch (err) {
      console.error(`[AutomationEngine] Action failed for node ${node.id}:`, err);
    }
    return {};
  }

  /**
   * Handles AI Auto-Responder logic for incoming SMS
   */
  private async handleAIAutoResponder(data: any) {
    const userId = data.user_id;
    const phone = data.phone_number;
    const content = data.content;

    if (!userId || !phone || !supabase) return;

    try {
      // 1. Check if AI Responder is enabled and get preferences
      const { data: prefs } = await supabase
        .from('agent_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (!prefs?.sms_auto_responder_enabled) return;

      console.log(`[AI Auto-Responder] Processing for ${phone}...`);

      // 2. Safety Check: Rule-Based Logic
      // We removed the 24h limit as requested.
      
      // 3. Gather Context: Conversation History & Lead Data
      const { data: history } = await supabase
        .from('sms_messages')
        .select('content, direction, created_at')
        .eq('phone_number', phone)
        .order('created_at', { ascending: false })
        .limit(10);

      const { data: lead } = await supabase
        .from('leads')
        .select('*')
        .eq('phone', phone)
        .maybeSingle();

      const conversationContext = (history || [])
        .reverse()
        .map((m: any) => `${m.direction === 'inbound' ? 'Lead' : 'Agent'}: ${m.content}`)
        .join('\n');

      // 4. Construct AI Prompt & Apply Rules
      let rulesPrompt = "";
      if (prefs.sms_ai_no_scheduling) {
        rulesPrompt += "\n- IMPORTANT: Do NOT suggest specific times or try to schedule an appointment. Keep it to general discovery.";
      }
      if (prefs.sms_ai_custom_rules) {
        rulesPrompt += `\n- Additional Rules: ${prefs.sms_ai_custom_rules}`;
      }

      let systemPrompt = '';
      if (prefs.sms_auto_responder_tone === 'Professional') {
        systemPrompt = "You are a professional, efficient, and helpful real estate assistant. Keep responses brief, polite, and focused on booking a call or gathering info.";
      } else if (prefs.sms_auto_responder_tone === 'Friendly') {
        systemPrompt = "You are a warm, casual, and relatable assistant. Use a friendly tone, occasionally use an emoji, and make the lead feel comfortable while being helpful.";
      } else if (prefs.sms_auto_responder_tone === 'Custom') {
        systemPrompt = prefs.sms_auto_responder_custom_prompt || "You are a helpful assistant.";
      }

      const fullPrompt = `
      ${systemPrompt}
      ${rulesPrompt}
      
      LEAD INFO:
      Name: ${lead?.name || 'Unknown'}
      Status: ${lead?.status || 'New'}
      Address: ${lead?.address || 'N/A'}
      
      CONVERSATION HISTORY:
      ${conversationContext}
      
      LATEST MESSAGE FROM LEAD:
      "${content}"
      
      TASK: Generate a natural, helpful SMS response (under 160 chars). 
      DO NOT include "Agent:" or "Lead:" prefix. Just the message body.
      If the user is asking something you don't know, tell them the agent will reach out shortly.
      `;

      // 5. Generate Response
      const aiResponse = await processPrompt(fullPrompt, { lead, history }, 'gemini-1.5-flash');
      const responseText = aiResponse.response.trim();

      if (!responseText) return;

      // 5.5 Check if confirmation is required
      const isScheduling = responseText.toLowerCase().includes('schedule') || 
                          responseText.toLowerCase().includes('calendar') || 
                          responseText.toLowerCase().includes('available') || 
                          responseText.toLowerCase().includes('meeting');
      
      const needsConfirmation = prefs.sms_ai_confirm_before_sending || 
                                (isScheduling && !prefs.sms_ai_no_scheduling) ||
                                prefs.sms_ai_explicit_only;

      if (needsConfirmation) {
        console.log(`[AI Auto-Responder] Response for ${phone} needs approval. Creating notification.`);
        await supabase.from('notifications').insert([{
          user_id: userId,
          type: 'system',
          title: 'AI Response Needs Approval',
          message: `Draft for ${phone}: "${responseText}"`,
          data: {
            type: 'ai_confirmation',
            phone: phone,
            draft: responseText,
            lead_id: lead?.id,
            original_msg: content
          }
        }]);
        return;
      }

      // 6. Send SMS (Prioritize Twilio if configured)
      const fromNumber = Array.isArray(prefs.twilio_phone_numbers) && prefs.twilio_phone_numbers.length > 0 
        ? prefs.twilio_phone_numbers[0] 
        : undefined;

      console.log(`[AI Auto-Responder] Sending response to ${phone}: ${responseText}`);
      
      const res = await sendSMS(phone, responseText, {
        userId,
        fromNumber,
        carrier: prefs.carrier
      });

      if (res.success) {
        // Log to timeline if lead exists
        if (lead) {
          await leadsService.addTimeline(lead.id, {
            type: 'sms',
            content: `AI Auto-Response Sent: ${responseText}`,
            timestamp: new Date().toISOString(),
            user: 'AI Assistant'
          });
        }
        
        // Note: The sendSMS or the edge function should already handle inserting into sms_messages.
        // We append a hidden tag to the content in our search filter (ilike) or just rely on a new column.
        // For now, let's just insert it manually with a hidden marker if needed, or check logs.
        // Actually, let's update the message with a marker.
        const { data: sentMsg } = await supabase
          .from('sms_messages')
          .select('id')
          .eq('user_id', userId)
          .eq('phone_number', phone)
          .eq('direction', 'outbound')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
          
        if (sentMsg) {
          await supabase
            .from('sms_messages')
            .update({ content: `${responseText} (AI Responder)` })
            .eq('id', sentMsg.id);
        }
      }

    } catch (err) {
      console.error('[AI Auto-Responder] Error:', err);
    }
  }

  private parseTemplate(template: string = '', context: any): string {
    return template.replace(/\{\{(.*?)\}\}/g, (_, key) => {
      const trimmedKey = key.trim();
      
      // Handle nested paths like lead.name
      const parts = trimmedKey.split('.');
      let value = context;
      for (const part of parts) {
        value = value?.[part];
      }

      // Fallback to top-level if nested not found
      if (value === undefined) {
        value = context[trimmedKey];
      }

      return value !== undefined ? String(value) : `{{${trimmedKey}}}`;
    });
  }
}

export const automationEngine = AutomationEngine.getInstance();

